/**
 * Local smoke against `transpais-booking-api` (Fake inventory + stub JWT).
 * Enable with REACT_APP_USE_LOCAL_BOOKING_API=1 and point
 * REACT_APP_API_TRANSPAIS=http://127.0.0.1:8000
 */

let cachedStubJwt = ''

export function isLocalBookingApiEnabled() {
  return String(process.env.REACT_APP_USE_LOCAL_BOOKING_API || '').trim() === '1'
}

export function getLocalBookingStubSecret() {
  return String(process.env.REACT_APP_BOOKING_AUTH_STUB_SECRET || 'change-me-local-stub-secret')
}

export function setLocalBookingStubJwt(token) {
  cachedStubJwt = typeof token === 'string' ? token : ''
}

export function getLocalBookingStubJwt() {
  return cachedStubJwt
}

export function bookingClientHeaders(extra = {}) {
  if (!isLocalBookingApiEnabled()) {
    return { ...extra }
  }
  return {
    'X-Client-Id': 'kiosk',
    ...extra,
  }
}

/**
 * Prefer stub JWT for local booking API; keep real TranspaisID token otherwise.
 */
export function resolveBookingAuthToken(explicitToken) {
  if (isLocalBookingApiEnabled() && cachedStubJwt) {
    return cachedStubJwt
  }
  return explicitToken || ''
}

let ensureStubPromise = null

/**
 * Awaitable gate so booking GETs/POSTs never send the real TranspaisID JWT in local mode.
 * @param {string} accessToken
 * @param {(token: string) => Record<string, unknown>} decodeFn
 */
export function waitForLocalBookingStub(accessToken, decodeFn) {
  if (!isLocalBookingApiEnabled()) {
    return Promise.resolve(null)
  }
  if (cachedStubJwt) {
    return Promise.resolve(cachedStubJwt)
  }
  if (!accessToken) {
    return Promise.reject(new Error('Missing TranspaisID token to mint local booking stub JWT'))
  }
  if (!ensureStubPromise) {
    ensureStubPromise = ensureLocalBookingStubFromAccessToken(accessToken, decodeFn)
      .catch((error) => {
        ensureStubPromise = null
        throw error
      })
  }
  return ensureStubPromise
}

function base64UrlEncodeBytes(bytes) {
  let binary = ''
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  for (let i = 0; i < arr.length; i += 1) {
    binary += String.fromCharCode(arr[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function base64UrlEncodeJson(value) {
  const json = JSON.stringify(value)
  return base64UrlEncodeBytes(new TextEncoder().encode(json))
}

/**
 * HS256 stub JWT compatible with booking-api AUTH_DRIVER=stub.
 * @param {Record<string, unknown>} claims
 * @param {number} [ttlSeconds]
 */
export async function mintLocalBookingStubJwt(claims, ttlSeconds = 60 * 60 * 12) {
  const secret = getLocalBookingStubSecret()
  if (!secret) {
    throw new Error('REACT_APP_BOOKING_AUTH_STUB_SECRET is empty')
  }
  if (!window.crypto?.subtle) {
    throw new Error('WebCrypto is required to mint local booking stub JWT')
  }

  const now = Math.floor(Date.now() / 1000)
  const header = { typ: 'JWT', alg: 'HS256' }
  const payload = {
    iat: now,
    exp: now + ttlSeconds,
    ...claims,
  }

  const encodedHeader = base64UrlEncodeJson(header)
  const encodedPayload = base64UrlEncodeJson(payload)
  const signingInput = `${encodedHeader}.${encodedPayload}`
  const key = await window.crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  const signature = await window.crypto.subtle.sign('HMAC', key, new TextEncoder().encode(signingInput))
  const token = `${signingInput}.${base64UrlEncodeBytes(signature)}`
  setLocalBookingStubJwt(token)
  return token
}

/**
 * Mint stub JWT from an existing TranspaisID access token payload (`sub` required).
 * @param {string} accessToken
 * @param {(token: string) => Record<string, unknown>} decodeFn jwt_decode
 */
export async function ensureLocalBookingStubFromAccessToken(accessToken, decodeFn) {
  if (!isLocalBookingApiEnabled() || !accessToken) {
    return null
  }
  const decoded = decodeFn(accessToken)
  const sub = decoded?.sub
  if (!sub) {
    throw new Error('Cannot mint local booking stub JWT without sub claim')
  }
  return mintLocalBookingStubJwt({
    sub,
    email: decoded.email || 'kiosk-local@transpais.test',
    nombre: decoded.nombre || decoded.given_name || 'Kiosk',
    paterno: decoded.paterno || decoded.family_name || 'Local',
  })
}
